import Axios from 'axios';

import { getFormData } from '$/lib/utils';
import { Country }     from '$/lib/Address';

import { createEntity, Entity } from '$/entities/BaseEntity';
import { FileStatus }           from '$/entities/File';
import { LandlordAssistFile, LandlordAssistProperties } from '$/entities/LandlordAssistFile';

import { AIEvalQuestion, EvaluationParams, FileStore as FileStoreCommon } from '$/common/entities/FileStore';
export * from '$/common/entities/FileStore';

@Entity('fileStore')
export class FileStore extends FileStoreCommon {

	override async getFiles({ lastFile, limit = 20 }: { lastFile?: LandlordAssistFile | string; limit?: number } = {}): Promise<LandlordAssistFile[]> {
		const lastFileId = typeof lastFile === 'string' ? lastFile : lastFile?.externalId;
		const params     = _.compactObject({ lastFile : lastFileId, limit });
		const rawFiles   = (await Axios.get(FileStore.collectionUrl(`${this.id}/files`), { params })).data;
		return rawFiles.map(rawFile => createEntity(rawFile, { possibleClass : LandlordAssistFile }));
	}

	/**
	 * Upload the given files and add them to the file store.
	 * @param files - The files to upload.
	 * @param properties - The properties to assign to the files.
	 */
	async uploadFiles(files: LandlordAssistFile[], properties: LandlordAssistProperties, progressUpdate?: (progress: number) => void) {
		const formData = getFormData(_.compactObject(properties), ...files);
		if (_.isEmpty(Array.from(formData.values()))) {
			return;
		}

		await Axios.post(FileStore.collectionUrl(`${this.id}/files/upload`), formData, { onUploadProgress : progressEvent => {
			progressUpdate?.(progressEvent.loaded / progressEvent.total);
		} });
	}

	override async addFiles(files: PossibleArray<LandlordAssistFile>) {
		files = _.castArray(files);
		if (files.some(file => file.status !== FileStatus.Saved)) {
			throw new Error("Cannot add files that haven't been uploaded. Use uploadFiles instead.");
		}

		await Axios.post(FileStore.collectionUrl(`${this.id}/files`), { fileIds : files.map(file => file.id) });
	}

	override async addFilesFromFileStore(sourceFileStore: FileStore) {
		await Axios.post(FileStore.collectionUrl(`${this.id}/files`), { sourceFileStoreId : sourceFileStore.id });
	}

	override async removeFiles(files: PossibleArray<LandlordAssistFile>) {
		await Axios.delete(FileStore.collectionUrl(`${this.id}/files`), { data : { fileIds : _.castArray(files).map(file => file.id) } });
	}

	override async publish() {
		await Axios.post(FileStore.collectionUrl(`${this.id}/publish`));
		await this.reload();
	}

	override async runEvaluation(params: EvaluationParams): Promise<AIEvalQuestion[]> {
		return (await Axios.post(FileStore.collectionUrl(`${this.id}/evaluation-answers`), params)).data as AIEvalQuestion[];
	}

	override async getNumFiles(): Promise<number> {
		return (await Axios.get(FileStore.collectionUrl(`${this.id}/num-files`))).data;
	}

	static override async getAvailableRegions(fileStore?: FileStore): Promise<{ country: Country; region: string }[]> {
		return (await Axios.get(FileStore.collectionUrl('/available-regions'), { params : { id : fileStore?.id } })).data;
	}

	static override async findByExternalId(externalId: string): Promise<FileStore> {
		return createEntity((await Axios.get(FileStore.collectionUrl(`/${externalId}`))).data, { possibleClass : this });
	}

	static override async findAllExternal(): Promise<FileStore[]> {
		const rawFileStores = (await Axios.get(FileStore.collectionUrl('/external'))).data;
		return rawFileStores.map(rawFileStore => createEntity(rawFileStore, { possibleClass : this }));
	}

}

